import React from "react";
import PropTypes from "prop-types";
import { Grid, Card, Box, Typography, Chip, Avatar, Divider } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PhonelinkIcon from "@mui/icons-material/Phonelink";
import { WhatsApp } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function ProfileSection({ person, title, isSpouse = false, patientData }) {
  const getGenderDisplay = (gender) => {
    switch (gender) {
      case "M":
        return "Male";
      case "F":
        return "Female";
      case "O":
        return "Other";
      case "U":
        return "Unspecified";
      default:
        return "N/A";
    }
  };

  const getGenderColor = (gender) => {
    switch (gender) {
      case "M":
        return "success";
      case "F":
        return "info";
      case "O":
        return "warning";
      case "U":
        return "default";
      default:
        return "default";
    }
  };

  const getPatientInitials = (name) => {
    if (!name) return "P";
    const names = name.trim().split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const getAvatarColor = (gender) => {
    switch (gender) {
      case "M":
        return "#1976d2"; // Blue for male
      case "F":
        return "#e91e63"; // Pink for female
      case "O":
        return "#ff9800"; // Orange for other
      case "U":
        return "#757575"; // Gray for unspecified
      default:
        return "#9e9e9e"; // Default gray
    }
  };

  const getPhoneIcon = (phoneType) => {
    switch (phoneType?.toLowerCase()) {
      case "mobile":
        return <PhonelinkIcon sx={{ mr: 1, color: "#00798E" }} />;
      case "land":
        return <LocalPhoneIcon sx={{ mr: 1, color: "#00798E" }} />;
      case "whatsapp":
        return <WhatsApp sx={{ mr: 1, color: "#00798E" }} />;
      default:
        return <PhoneIcon sx={{ mr: 1, color: "#00798E" }} />;
    }
  };

  if (!person) return null;

  return (
    <Grid item xs={12} md={4} lg={4} key={person.id}>
      <Card sx={{ height: "100%" }}>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="logo"
          borderRadius="lg"
          coloredShadow="logo"
        >
          <MDTypography variant="h6" color="white">
            {title}
          </MDTypography>
        </MDBox>

        <MDBox p={3}>
          {/* Profile Header with Avatar */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Avatar
              src={
                person.profile_picture_base_url && person.profile_picture_path
                  ? person.profile_picture_base_url + person.profile_picture_path
                  : ""
              }
              alt={person.full_name || "Person"}
              sx={{
                width: 100,
                height: 100,
                bgcolor: getAvatarColor(person.gender),
                border: "3px solid #e0e0e0",
                fontSize: "2rem",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              {(!person.profile_picture_base_url || !person.profile_picture_path) &&
                getPatientInitials(person.full_name)}
            </Avatar>

            {/* Name */}
            <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
              {person.full_name || "N/A"}
            </Typography>

            {/* Gender Badge */}
            <Chip
              label={getGenderDisplay(person.gender)}
              size="medium"
              color={getGenderColor(person.gender)}
              sx={{ mb: 2 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Personal Information */}
          <Box mb={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: "#00798E" }}>
              Personal Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PersonIcon sx={{ mr: 1, color: "#00798E" }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1">{person.date_of_birth || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={2}>
                  <WorkIcon sx={{ mr: 1, color: "#00798E" }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Occupation
                    </Typography>
                    <Typography variant="body1">{person.occupation || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={2}>
                  <LocationOnIcon sx={{ mr: 1, color: "#00798E" }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Nationality
                    </Typography>
                    <Typography variant="body1">{person.nationality || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Email Information */}
          {person.email_address && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: "#00798E" }}>
                  Email
                </Typography>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 1, color: "#00798E" }} />
                  <Typography variant="body1">{person.email_address}</Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Phone Information */}
          {patientData.phones && patientData.phones.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: "#00798E" }}>
                  Phone Numbers
                </Typography>
                {patientData.phones.map((phone, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1}>
                    {getPhoneIcon(phone.phone_type)}
                    <Box>
                      <Typography variant="body1">{phone.phone_number}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {phone.phone_type || "Phone"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Address Information */}
          {patientData.addresses && patientData.addresses.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box mb={3}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: "#00798E" }}>
                  Addresses
                </Typography>
                {patientData.addresses.map((address, index) => (
                  <Box key={index} display="flex" alignItems="flex-start" mb={2}>
                    <HomeIcon sx={{ mr: 1, mt: 0.5, color: "#00798E" }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {address.address_type || "Address"}
                      </Typography>
                      <Typography variant="body1">
                        {[
                          address.address_line_1,
                          address.address_line_2,
                          address.city,
                          address.state,
                          address.postal_code,
                          address.country,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </MDBox>
      </Card>
    </Grid>
  );
}

ProfileSection.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    profile_picture_base_url: PropTypes.string,
    profile_picture_path: PropTypes.string,
    full_name: PropTypes.string,
    gender: PropTypes.string,
    date_of_birth: PropTypes.string,
    occupation: PropTypes.string,
    nationality: PropTypes.string,
    email_address: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
  isSpouse: PropTypes.bool,
  patientData: PropTypes.shape({
    phones: PropTypes.arrayOf(
      PropTypes.shape({
        phone_number: PropTypes.string,
        phone_type: PropTypes.string,
      })
    ),
    addresses: PropTypes.arrayOf(
      PropTypes.shape({
        address_type: PropTypes.string,
        address_line_1: PropTypes.string,
        address_line_2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        postal_code: PropTypes.string,
        country: PropTypes.string,
      })
    ),
  }),
};

ProfileSection.defaultProps = {
  person: null,
  isSpouse: false,
  patientData: {
    phones: [],
    addresses: [],
  },
};

export default ProfileSection;
